/**
 * /me/attendance — M3X.ATTENDANCE F.28 hub for employees.
 *
 * Lists the 7 Solvait AI assistant functions (Q2 lock) as callable
 * cards. Each renders an interactive panel calling the backend and
 * showing the structured response — the LLM-narrated polish lands
 * in M3X.1 v2 Khidmat orchestrator.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../components/ui";

const ASSIST_FUNCTIONS = [
  {
    key: "summary",
    title: "Summarize my month",
    description: "Total worked days, late minutes, OT, WFH, perfect-attendance flag",
    endpoint: "/api/me/attendance/summary",
  },
  {
    key: "propose_excuse",
    title: "Propose an excuse",
    description: "Suggests the appropriate excuse type based on late/early flag",
    endpoint: "/api/me/attendance/propose-excuse",
  },
  {
    key: "explain_payroll",
    title: "Explain my payroll delta",
    description: "Breaks down why this period differs from last",
    endpoint: "/api/me/attendance/explain-payroll-delta",
  },
  {
    key: "forecast_ot",
    title: "Forecast OT earnings",
    description: "Extrapolate OT minutes for the remainder of the month",
    endpoint: "/api/me/attendance/forecast-ot",
  },
  {
    key: "suggest_wfh",
    title: "Suggest a WFH day",
    description: "Next available Wed/Thu within 7 days",
    endpoint: "/api/me/attendance/suggest-wfh-day",
  },
  {
    key: "check_balance",
    title: "Check my balances",
    description: "Leave remaining + pending/approved OT hours",
    endpoint: "/api/me/attendance/check-balance",
  },
  {
    key: "ask_concierge",
    title: "Ask Solvait Concierge",
    description: "Free-form question routed to Khidmat",
    endpoint: "/api/me/attendance/ask-concierge",
  },
];

export function EmployeeAttendanceHubPage() {
  const { t } = useTranslation();
  const [employeeId, setEmployeeId] = useState("");
  const [results, setResults] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function callFn(fnKey: string, endpoint: string) {
    if (!employeeId) return;
    setBusy(fnKey);
    try {
      const qs = new URLSearchParams({ employee_id: employeeId });
      const res = await fetch(`${endpoint}?${qs}`);
      const j = await res.json();
      setResults((r) => ({ ...r, [fnKey]: j }));
    } catch (e) {
      setResults((r) => ({ ...r, [fnKey]: { error: String(e) } }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(
          "employee_att_hub.title",
          "My Attendance & Solvait AI",
        )}
        subtitle={t(
          "employee_att_hub.subtitle",
          "F.28 — 7 self-service AI assistant functions per Q2 lock.",
        )}
      />

      <div className="rounded-lg border border-slate-200 bg-surface-default p-4">
        <label className="block text-sm">
          <span className="text-xs text-text-secondary">
            Your employee_id (UUID)
          </span>
          <input
            type="text"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="mt-1 w-full rounded border border-border-default p-2 font-mono text-xs"
            placeholder="paste your employee UUID"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {ASSIST_FUNCTIONS.map((fn) => (
          <div
            key={fn.key}
            className="rounded-lg border border-slate-200 bg-surface-default p-4"
          >
            <h3 className="text-sm font-semibold">
              ✦ {t(`employee_att_hub.${fn.key}_title`, fn.title)}
            </h3>
            <p className="mt-1 text-xs text-text-tertiary">
              {fn.description}
            </p>
            <button
              type="button"
              onClick={() => callFn(fn.key, fn.endpoint)}
              disabled={!employeeId || busy === fn.key}
              className="mt-2 rounded bg-brand-blue px-3 py-1 text-xs text-white disabled:opacity-50"
            >
              {busy === fn.key ? "…" : "Run"}
            </button>
            {results[fn.key] !== undefined && (
              <pre className="mt-3 max-h-40 overflow-auto rounded bg-surface-muted p-2 text-[10px]">
                {JSON.stringify(results[fn.key], null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default EmployeeAttendanceHubPage;
